import React, { useState } from 'react';
import {
  GlobeAltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';

export const useRegister = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    postalCode: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formStatus, setFormStatus] = useState<{ error: string; success: boolean; loading: boolean }>({ error: '', success: false, loading: false });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'username') {
      // Only allow lowercase letters, numbers, and hyphens
      const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData({ ...formData, [name]: cleaned });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const getUsernameError = (): string | null => {
    const u = formData.username;
    if (!u) return null;
    if (u.length < 10) return `${10 - u.length} more character${10 - u.length === 1 ? '' : 's'} needed (min 10).`;
    if (u.length > 40) return 'Too long (max 40 characters).';
    if (/^-/.test(u) || /-$/.test(u)) return 'Must start and end with a letter or number.';
    if (/--/.test(u)) return 'Cannot contain consecutive hyphens (--).';
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(u)) return 'Only lowercase letters, numbers, and hyphens allowed.';
    return null;
  };

  const usernameError = getUsernameError();
  const usernameValid = formData.username.length >= 10 && !usernameError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus(prev => ({ ...prev, error: '' }));

    if (!agreedToTerms) {
      setFormStatus(prev => ({ ...prev, error: 'You must agree to the Terms & Conditions.' }));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setFormStatus(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }
    if (formData.password.length < 6) {
      setFormStatus(prev => ({ ...prev, error: 'Password must be at least 6 characters.' }));
      return;
    }
    if (formData.username.length < 10) {
      setFormStatus(prev => ({ ...prev, error: 'Username must be at least 10 characters.' }));
      return;
    }
    if (formData.username.length > 40) {
      setFormStatus(prev => ({ ...prev, error: 'Username must be at most 40 characters.' }));
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(formData.username)) {
      setFormStatus(prev => ({ ...prev, error: 'Username can only contain lowercase letters, numbers, and hyphens. Must start and end with a letter or number.' }));
      return;
    }
    if (/--/.test(formData.username)) {
      setFormStatus(prev => ({ ...prev, error: 'Username cannot contain consecutive hyphens.' }));
      return;
    }

    setFormStatus(prev => ({ ...prev, loading: true }));

    try {
      const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://127.0.0.1:8001/api'
        : '/api';

      const res = await fetch(`${apiBase}/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_person: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postalCode,
          username: formData.username,
          password: formData.password,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setFormStatus(prev => ({ ...prev, success: true }));
      } else {
        setFormStatus(prev => ({ ...prev, error: data.error || 'Registration failed. Please try again.' }));
      }
    } catch {
      setFormStatus(prev => ({ ...prev, error: 'Connection error. Please try again.' }));
    } finally {
      setFormStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const benefits = [
    { icon: GlobeAltIcon, title: 'Branded Portal', desc: 'Your own subdomain at yourbakery.sourdoughsy.com' },
    { icon: UserGroupIcon, title: 'Customer Management', desc: 'Manage customers, orders, and accounts in one place' },
    { icon: ChartBarIcon, title: 'Financial Reports', desc: 'Track revenue, balances, and growth with live charts' },
    { icon: ShieldCheckIcon, title: 'Secure & Reliable', desc: 'Enterprise-grade security for your bakery data' },
  ];

  return {
    formData,
    formStatus,
    showPassword,
    setShowPassword,
    handleChange,
    usernameError,
    usernameValid,
    handleSubmit,
    benefits,
    agreedToTerms,
    setAgreedToTerms,
  };
};
